import React from 'react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import PromoBanner from '../components/PromoBanner';
import { CheckCircle, Clock, Book, Award } from 'lucide-react';

const AWSProjectsBundlePage = () => {
  const bundle = {
    id: 1,
    title: "AWS Projects Bundle",
    description: "Master AWS with comprehensive hands-on projects",
    originalPrice: 207,
    discountedPrice: 149,
    savings: 58,
    courses: [
      {
        title: "Build: 5 Beginner AWS Cloud Projects",
        price: 69,
        image: "/course-covers/2.png"
      },
      {
        title: "Build: 5 Intermediate AWS Cloud Projects",
        price: 69,
        image: "/course-covers/3.png"
      },
      {
        title: "Build: 5 Advanced AWS Cloud Projects",
        price: 69,
        image: "/course-covers/4.png"
      }
    ],
    image: "/bundle-covers/aws-projects.jpg",
    features: [
      "15 comprehensive AWS projects",
      "Beginner to advanced progression",
      "Real-world scenarios and use cases",
      "Portfolio-ready project examples",
      "Step-by-step project guidance",
      "Lifetime access to all projects"
    ]
  };

  const percentageSaved = Math.round((bundle.savings / bundle.originalPrice) * 100);

  return (
    <div className="min-h-screen bg-gray-50">
      <PromoBanner />
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="container">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Left Side - Bundle Info */}
              <div>
                <div className="inline-flex items-center gap-2 bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm font-medium mb-6">
                  <Award size={16} />
                  Popular Choice
                </div>
                
                <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                  {bundle.title}
                </h1>
                
                <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                  {bundle.description}
                </p>
                
                {/* Pricing */}
                <div className="mb-8">
                  <div className="flex items-center gap-4 mb-2">
                    <span className="text-3xl font-bold text-gray-500 line-through">${bundle.originalPrice}</span>
                    <span className="text-4xl font-bold text-green-600">${bundle.discountedPrice}</span>
                    <span className="bg-green-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                      Save {percentageSaved}%
                    </span>
                  </div>
                  <p className="text-lg text-gray-600">You save ${bundle.savings}!</p>
                </div>
                
                {/* Features */}
                <div className="mb-8">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">What's included:</h3>
                  <ul className="space-y-3">
                    {bundle.features.map((feature, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <CheckCircle className="text-green-500 mt-0.5 flex-shrink-0" size={20} />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                {/* CTA Button */}
                <a 
                  href="https://learn.zerotocloud.co/p/aws-projects-bundle"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full bg-gradient-to-r from-green-500 to-emerald-600 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:shadow-lg transition-all duration-200 inline-block text-center"
                >
                  Get Projects Bundle - ${bundle.discountedPrice}
                </a>
              </div>
              
              {/* Right Side - Bundle Image */}
              <div className="relative">
                <img 
                  src={bundle.image}
                  alt={bundle.title}
                  className="w-full rounded-xl shadow-2xl"
                />
                <div className="absolute top-4 right-4 bg-green-500 text-white px-4 py-2 rounded-full text-sm font-medium">
                  Save ${bundle.savings}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Courses Included Section */}
      <section className="py-16 bg-white">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
              Projects Included ({bundle.courses.length} Course Bundles)
            </h2>
            
            <div className="space-y-6">
              {bundle.courses.map((course, index) => (
                <div key={index} className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                  <div className="flex items-center gap-6">
                    <img 
                      src={course.image}
                      alt={course.title}
                      className="w-20 h-20 object-contain bg-white rounded-lg border border-gray-200"
                    />
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        {course.title}
                      </h3>
                      <div className="flex items-center gap-4">
                        <span className="text-2xl font-bold text-gray-900">
                          ${course.price}
                        </span>
                        <span className="text-sm text-green-600 font-medium">
                          ✓ Included in bundle
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Bundle Summary */}
            <div className="mt-12 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-8 border border-blue-200">
              <div className="text-center">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Bundle Summary</h3>
                <div className="flex justify-center items-center gap-8 mb-6">
                  <div>
                    <div className="text-lg text-gray-600">Individual Price:</div>
                    <div className="text-2xl font-bold text-gray-500 line-through">${bundle.originalPrice}</div>
                  </div>
                  <div className="text-3xl text-gray-400">→</div>
                  <div>
                    <div className="text-lg text-gray-600">Bundle Price:</div>
                    <div className="text-3xl font-bold text-green-600">${bundle.discountedPrice}</div>
                  </div>
                </div>
                
                <button className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-12 py-4 rounded-lg font-semibold text-xl hover:shadow-lg transition-all duration-200">
                  Start Building Projects - ${bundle.discountedPrice}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AWSProjectsBundlePage;